import React, { useState, useRef, useEffect } from 'react'
import imageCompression from 'browser-image-compression'
import ApiService from '../../ApiService'
import { parseAxiosError } from '../../util/parseHelper'
import { useHistory } from 'react-router-dom'
import url from '../../util/routeURL'
import { AttachFile } from '@material-ui/icons'
import MuiAlert from '@material-ui/lab/Alert'
import Snackbar from '@material-ui/core/Snackbar'
import './Message.css'

const TEAM = {
  CUSTOMER_CARE: 'customer-care',
  MEDICAL_TEAM: 'medical-team',
  LABELS: {
    'customer-care': 'To: Customer Care',
    'medical-team': 'To: Clinical Team',
    SUPPORT_TITLE: 'Support',
    MEDICAL_TITLE: 'Medical',
    SUPPORT_ENUM: 'SUPPORT',
    MEDICAL_ENUM: 'CLINICIAN',
  },
}

const SELECT_TEAM = 'select'

function orderToOption(order) {
  const li = order?.line_item
  const label = li
    ? `Order: ${order.id} / ${li?.name ?? 'N/A'} [qty x ${li?.quantity ?? 0}]`
    : `Order: ${order.id}`
  return { id: order.id, label }
}

function ordersToOptions(orders) {
  return orders.map(orderToOption)
}

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />
}

const NewMessage = () => {
  const history = useHistory()

  const [selectedAttachment, setSelectedAttachment] = useState([])
  const [attachedFileS3URLs, setAttachedFileS3URLs] = useState([])
  const [snackOpen, setSnackOpen] = useState(false)
  const [snackSeverity, setSnackSeverity] = useState('')
  const [snackText, setSnackText] = useState('')
  const fileInputRef = useRef(null)

  const [selectedTeam, setSelectedTeam] = useState(SELECT_TEAM)
  const [orderList, setOrderList] = useState()
  const [selectedOrder, setSelectedOrder] = useState('')
  const [orderLoading, setOrderLoading] = useState(false)
  const [allOrders, setAllOrders] = useState([])

  const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async event => {
    const form = event.currentTarget
    event.preventDefault()

    if (selectedTeam === SELECT_TEAM) {
      return handleSnackOpen('Please select a recipient', 'error')
    }

    if (selectedTeam === TEAM.MEDICAL_TEAM && !selectedOrder) {
      return handleSnackOpen('Please select an order', 'error')
    }

    if (form.checkValidity() === false) {
      return event.stopPropagation()
    }

    const subject = document.getElementById('subject').value.trim()
    const content = document.getElementById('content').value.trim()

    if (!subject) {
      return handleSnackOpen('Subject cannot be empty', 'error')
    }
    if (!content) {
      return handleSnackOpen('Message cannot be empty', 'error')
    }

    setIsSubmitting(true)

    const msg = {
      subject,
      content,
      attachmentUrls: attachedFileS3URLs,
      groupId: selectedTeam === TEAM.CUSTOMER_CARE ? TEAM.LABELS.SUPPORT_ENUM : TEAM.LABELS.MEDICAL_ENUM,
      orderId: selectedOrder || null,
    }

    try {
      await ApiService.postMessage(msg)
      handleSnackOpen('Message sent successfully', 'success')
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } catch (ex) {
      console.error('Message submission failed', ex)
      handleSnackOpen('Failed to send message. Please try again.', 'error')
      setIsSubmitting(false)
    }
  }

  const handleAttachmentClick = () => {
    if (selectedAttachment?.length >= 5) {
      return handleSnackOpen('Only 5 files allowed at a time', 'error')
    }
    if (fileInputRef.current) fileInputRef.current.click()
  }

  const handleFileChange = async event => {
    const files = Array.from(event.target.files)
    if (!files.length) return

    for (const file of files) {
      if (selectedAttachment.length >= 5) {
        handleSnackOpen('Only 5 files allowed at a time', 'error')
        break
      }

      let uploadFile = file

      // Compress image files larger than 2MB
      if (file.size > 2 * 1024 * 1024) {
        if (file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png') {
          try {
            const compressedBlob = await imageCompression(file, {
              maxSizeMB: 2,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            })
            uploadFile = new File([compressedBlob], file.name, {
              type: file.type,
              lastModified: file.lastModified,
            })
          } catch (err) {
            console.warn('Compression failed:', err)
          }
        }
      }

      // Upload file and update state
      handleSnackOpen(`Uploading ${uploadFile.name}`, 'info')
      try {
        const resp = await ApiService.uploadFile(uploadFile, 'MESSAGE_ATTACHMENTS')
        const { url } = resp?.data?.body || {}
        uploadFile.url = url
        setSelectedAttachment(prev => [...prev, uploadFile])
        setAttachedFileS3URLs(prev => [...prev, url])
        handleSnackOpen(`${uploadFile.name} uploaded successfully`, 'success')
      } catch (err) {
        if (fileInputRef.current.value) fileInputRef.current.value = null
        handleSnackOpen(`Upload failed for ${uploadFile.name}: ${parseAxiosError(err)?.reason ?? 'unknown'}`, 'error')
      }
    }
  }

  const handleSnackOpen = (text, severity) => {
    setSnackSeverity(severity)
    setSnackText(text)
    setSnackOpen(true)
  }

  const handleSnackClose = () => {
    setSnackOpen(false)
  }

  function formatFileSize(size) {
    if (size < 1024) {
      return `(${size} B)`
    } else if (size < 1048576) {
      const kbSize = Math.round(size / 1024)
      return `(${kbSize} KB)`
    } else {
      const mbSize = Math.round(size / 1048576)
      return `(${mbSize} MB)`
    }
  }

  const removeFileFromAttachment = async index => {
    const fileName = selectedAttachment[index]?.url
    await ApiService.deleteFile('MESSAGE_ATTACHMENTS', fileName)
      .then(() => {
        const newAttachment = [...selectedAttachment]
        newAttachment.splice(index, 1)
        setSelectedAttachment(newAttachment)

        const s3Urls = [...attachedFileS3URLs]
        const indexOfFileName = s3Urls.indexOf(fileName)
        s3Urls.splice(indexOfFileName, 1)
        setAttachedFileS3URLs(s3Urls)
        handleSnackOpen('Success', 'success')
        if (fileInputRef.current.value) {
          fileInputRef.current.value = null
        }
      })
      .catch(error => {
        handleSnackOpen(parseAxiosError(error)?.reason, 'error')
      })
  }

  const getMessageOrder = async () => {
    try {
      setOrderLoading(true)
      const response = await ApiService.getOrderFromInbox()
      setAllOrders(response.data)

      // Initial default: full list (support)
      const orders = ordersToOptions(response.data)
      setOrderList(orders)
      if (orders.length > 0) {
        setSelectedOrder(orders[0].id)
      } else {
        setSelectedOrder('')
      }
    } catch (error) {
      console.error(error)
    } finally {
      setOrderLoading(false)
    }
  }

  const filterOrdersByTeam = team => {
    const filtered = team === TEAM.MEDICAL_TEAM ? allOrders.filter(order => order?.belugaVisitId) : allOrders
    const mapped = ordersToOptions(filtered)
    setOrderList(mapped)
    setSelectedOrder(mapped.length > 0 ? mapped[0].id : '')
  }

  useEffect(() => {
    getMessageOrder()
  }, [])

  return (
    <div className="new_msg_container">
      <form className="needs-validation new_msg_form" noValidate onSubmit={handleSubmit}>
        {isRecipientModalOpen && (
          <div className="recipient-modal-backdrop">
            <div className="recipient-modal">
              <p className="modal-title">Do you have a question?</p>
              <p className="modal-subtitle">Select the issue you’re having below:</p>
              <div className="recipient-options">
                <div
                  className={`recipient-tile ${selectedTeam === TEAM.CUSTOMER_CARE ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedTeam(TEAM.CUSTOMER_CARE)
                    filterOrdersByTeam(TEAM.CUSTOMER_CARE)
                    setIsRecipientModalOpen(false)
                  }}
                >
                  <p className="tile-title">{TEAM.LABELS.SUPPORT_TITLE}</p>
                  <ul>
                    <li>Ordering a test kit</li>
                    <li>Prescription</li>
                    <li>Shipping delays or updates</li>
                    <li>Refunds</li>
                  </ul>
                </div>
                <div
                  className={`recipient-tile ${selectedTeam === TEAM.MEDICAL_TEAM ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedTeam(TEAM.MEDICAL_TEAM)
                    filterOrdersByTeam(TEAM.MEDICAL_TEAM)
                    setIsRecipientModalOpen(false)
                  }}
                >
                  <p className="tile-title">{TEAM.LABELS.MEDICAL_TITLE}</p>
                  <ul>
                    <li>Understanding your test results</li>
                    <li>Discussing medication instructions</li>
                    <li>Reviewing symptoms or side effects</li>
                    <li>Following up on progress or care</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="send-overhead">
          {!isRecipientModalOpen && selectedTeam !== SELECT_TEAM && (
            <div className="new_msg_input" style={{ marginTop: '10px' }}>
              <select
                id="teamSelect"
                name="team"
                className="msg_select"
                value={selectedTeam}
                onChange={e => {
                  const value = e.target.value
                  setSelectedTeam(value)
                  filterOrdersByTeam(value)
                }}
              >
                <option value={SELECT_TEAM}>Select</option>
                <option value={TEAM.CUSTOMER_CARE}>{TEAM.LABELS[TEAM.CUSTOMER_CARE]}</option>
                <option value={TEAM.MEDICAL_TEAM}>{TEAM.LABELS[TEAM.MEDICAL_TEAM]}</option>
              </select>
            </div>
          )}
          {orderLoading ? (
            <div className="new_msg_input" style={{ marginTop: '10px' }}>
              <p>Loading orders...</p>
            </div>
          ) : (
            orderList?.length > 0 && (
              <div className="new_msg_input" style={{ marginTop: '10px' }}>
                <select id="orderSelect" name="order" className="msg_select" value={selectedOrder} onChange={e => setSelectedOrder(e.target.value)}>
                  <option value="">Select an order</option>
                  {orderList.map(order => (
                    <option key={order.id} value={order.id}>
                      {order.label}
                    </option>
                  ))}
                </select>
              </div>
            )
          )}

          <div className="new_msg_input">
            <input placeholder="Subject" type="text" id="subject" name="subject" className="msg_sub_input" />
          </div>
        </div>

        {/* Body */}
        <textarea placeholder="Type your message here" name="body" id="content" className="msg_inputs send_textarea" />

        {/* Attachments */}
        {selectedAttachment.length > 0 && (
          <div className="attachment-section">
            {selectedAttachment.map((item, index) => (
              <div className="attachment-file" key={index}>
                <span className="file-name">{item.name}</span>
                <span className="file-size">{formatFileSize(item.size)}</span>
                <span className="remove-file" onClick={() => removeFileFromAttachment(index)}>
                  <i className="fa fa-times" aria-hidden="true"></i>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Send */}
        <div className="send-message">
          <button type="submit" className="replyButton sendButton" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send'}
          </button>
          <div onClick={handleAttachmentClick} className="attach-icon">
            <AttachFile style={{ fontSize: '2.2rem' }} />
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} multiple />
          </div>
        </div>

        <Snackbar open={snackOpen} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={handleSnackClose} severity={snackSeverity}>
            {snackText}
          </Alert>
        </Snackbar>
      </form>
    </div>
  )
}
export default NewMessage
